import React, { Fragment, useEffect, useState } from "react";

import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

import { rankItem } from "@tanstack/match-sorter-utils";
import { ChevronDown, Minus, Plus, Search, X } from "lucide-react";
import Pagination from "./Components/Pagination";

// Column Filter
const Filter = ({
  column,
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? "") as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + "list"}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
};
interface TableContainerProps {
  columns?: any;
  data?: any;
  tableclassName?: any;
  divclassName?: any;
  thclassName?: any;
  isrowBggreenData?: any;
  trclassName?: any;
  tableClass?: any;
  tdclassName?: any;
  theadclassName?: any;
  tbodyclassName?: any;
  isTfoot?: boolean;
  TableFarmerInvoise?: boolean;
  resiveWast?: boolean;
  isSelect?: boolean;
  isBordered?: boolean;
  customPageSize?: number;
  isGlobalFilter?: boolean;
  isPagination: boolean;
  PaginationClassName?: string;
  SearchPlaceholder?: string;
  customnumberfortable?: number;
}

const TableContainer = ({
  columns,
  data,
  tableclassName,
  theadclassName,
  divclassName,
  trclassName,
  thclassName,
  tdclassName,
  tbodyclassName,
  resiveWast,
  isTfoot,
  isSelect,
  isPagination,
  customPageSize,
  isrowBggreenData,
  isGlobalFilter,
  PaginationClassName,
  SearchPlaceholder,
  TableFarmerInvoise,
  customnumberfortable,
}: TableContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const {
    getHeaderGroups,
    getFooterGroups,
    getRowModel,
    getPageOptions,
    setPageIndex,
    setPageSize,
    getState,
    getCanPreviousPage,
    getCanNextPage,
    nextPage,
    previousPage,
  } = table;

  useEffect(() => {
    Number(customPageSize) && setPageSize(Number(customPageSize));
  }, [customPageSize, setPageSize]);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null); // تغییر نوع به string | null

  useEffect(() => {
    const handleResize = () => {
      setIsMobileOrTablet(window.innerWidth <= 768); // تبلت و موبایل
    };

    handleResize(); // هنگام بارگذاری صفحه بررسی شود
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const getVisibleColumns = () => {
    if (typeof window !== "undefined") {
      if (window.innerWidth < 640) return 3; // موبایل
      if (window.innerWidth < 1024) return 5; // تبلت
      const numberForTable = customnumberfortable ?? 8;
      return numberForTable; // دسکتاپ
    }
    return 3; // پیش‌فرض برای SSR
  };

  const visibleColumns = getVisibleColumns();
  return (
    <Fragment>
      <div className="grid grid-cols-12 lg:grid-cols-12 gap-3">
        {isGlobalFilter && (
          <label className="flex items-center mb-2 gap-x-2">
            جست‌وجو:
            <div className="relative">
              <DebouncedInput
                value={globalFilter ?? ""}
                onChange={(value) => {
                  if (typeof value === "string") {
                    setGlobalFilter(value.trim());
                  } else {
                    setGlobalFilter(String(value));
                  }
                }}
                className="py-1 pr-2 pl-8 text-sm text-topbar-item bg-topbar border border-topbar-border rounded placeholder:text-slate-400 form-control focus-visible:outline-0 min-w-[200px] focus:border-blue-400 group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:border-topbar-border-dark group-data-[topbar=dark]:placeholder:text-slate-500 group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:border-topbar-border-brand group-data-[topbar=brand]:placeholder:text-blue-300 group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:border-zink-500 group-data-[topbar=dark]:dark:text-zink-100"
              />
              <span className="absolute inset-y-0 left-2 flex items-center text-gray-400">
                {globalFilter ? (
                  <X className="h-4 w-4 cursor-pointer" onClick={() => setGlobalFilter("")} />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </span>
            </div>
          </label>
        )}

        {isSelect && (
          <div className="mt-[-3.4rem] p-2 absolute md:flex hidden left-72 justify-end col-span-12 lg:col-span-6">
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-600 dark:text-gray-300">
              <span>سطر</span>
              <div className="relative">
                <select
                  name="basic_tables_length"
                  aria-controls="basic_tables"
                  className="h-10 pl-3 pr-8 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent shadow-sm transition-all duration-200 ease-in-out hover:border-gray-400 dark:hover:border-gray-500 appearance-none cursor-pointer"
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option
                      key={size}
                      value={size}
                      className="bg-white dark:bg-gray-800"
                    >
                      {size}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                  <ChevronDown className="w-5 h-5 text-gray-400 dark:text-gray-400" />
                </div>
              </div>
            </label>
          </div>
        )}
      </div>

      <div className={divclassName}>
        <table className={tableclassName}>
          <thead className={`${theadclassName} cursor-pointer`}>
            {getHeaderGroups().map((headerGroup: any) => (
              <tr key={headerGroup.id} className={trclassName}>
                {headerGroup.headers.map((header: any, index: number) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={`${header.column.getCanSort()} ${thclassName} 
            ${index >= getVisibleColumns() ? "hidden" : ""}`} // ستون‌های مخفی
                    // onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : (
                      <React.Fragment>
                        {flexRender<any>(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {(
                          {
                            asc: " ",
                            desc: " ",
                          } as any
                        )[header.column.getIsSorted() as string] ?? null}
                      </React.Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody className={tbodyclassName}>
            {getRowModel().rows.map((row: any) => {
              const RowInvosAllZero = TableFarmerInvoise
                ? row.original.one_percent_deduction === 0 &&
                  row.original.pure_payable === 0 &&
                  row.original.remaining_receivable_payable_balance === 0 &&
                  row.original.total_farmers_price === 0 &&
                  row.original.total_guarantee_price === 0 &&
                  row.original.total_pesticide_cost === 0 &&
                  row.original.total_seed_cost === 0 &&
                  row.original.total_weight === 0 &&
                  row.original.payed == false
                : null;
              const isRowBgGreen = resiveWast
                ? (row.original.waste_delivered != 0 &&
                    row.original.waste_delivered != 0.0) ||
                  (row.original.waste_deposit_amount != 0 &&
                    row.original.waste_deposit_amount != 0.0)
                : (row.original.sugar_delivered != 0 &&
                    row.original.sugar_deposit_amount != 0.0) ||
                  row.original.sugar_deposit_amount != 0 ||
                  row.original.sugar_delivered != 0.0;

              return (
                <React.Fragment key={row.id}>
                  {/* ردیف اصلی */}
                  <tr
                    className={`${trclassName} ${
                      RowInvosAllZero == false && row?.original.payed == true
                        ? "bg-green-300 dark:bg-green-700"
                        : RowInvosAllZero
                        ? "bg-orange-300 dark:bg-gray-600 dark:text-gray-100"
                        : isrowBggreenData && isRowBgGreen
                        ? "bg-green-300 dark:bg-green-700"
                        : "bg-transparent"
                    }`}
                  >
                    {row.getVisibleCells().map((cell: any, index: number) => {
                      let cellClassName = `${tdclassName} ${
                        index >= getVisibleColumns() ? "hidden" : ""
                      }`;

                      return (
                        <td key={cell.id} className={cellClassName}>
                          {index === 0 ? (
                            <div className="flex items-center">
                              {openDropdown === row.id ? (
                                <Minus
                                  className="dark:text-white w-4 h-4 flex text-xs border-2 ml-2 dark:border-white text-blue-800 border-blue-800 rounded-full cursor-pointer"
                                  onClick={() => setOpenDropdown(null)}
                                />
                              ) : (
                                <Plus
                                  className="dark:text-white w-4 h-4 flex text-xs border-2 ml-2 dark:border-white text-blue-800 border-blue-800 rounded-full cursor-pointer"
                                  onClick={() => setOpenDropdown(row.id)}
                                />
                              )}
                              {flexRender<any>(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </div>
                          ) : (
                            flexRender<any>(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* ردیف دراپ‌دان */}
                  {openDropdown === row.id && (
                    <tr className="bg-transparent">
                      <td
                        colSpan={row.getVisibleCells().length}
                        className="pb-1"
                      >
                        <div className={`rounded shadow-md bg-transparent`}>
                          <div className="block">
                            {row
                              .getVisibleCells()
                              .map((cell: any, index: number) => {
                                let cellClassName = `flex dark:border-blue-900 hover:mr-1 hover:cursor-pointer border-blue-100 border-b flex-col w-full ${
                                  isRowBgGreen
                                    ? "bg-green-300 dark:text-gray-800"
                                    : "bg-white"
                                }`;

                                return (
                                  <div key={cell.id} className={cellClassName}>
                                    {index >= getVisibleColumns() && (
                                      <div className="flex w-full gap-4">
                                        <div className="font-bold lg:p-1 pb-1 shrink-0 w-1/3 md:w-1/4">
                                          {typeof cell.column.columnDef
                                            .header === "function"
                                            ? flexRender<any>(
                                                cell.column.columnDef.header,
                                                cell.getContext()
                                              )
                                            : cell.column.columnDef.header}
                                        </div>
                                        <div className="flex-1 sm:pt-0 mr-[4.6rem] lg:mr-0">
                                          {flexRender<any>(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>

          {isTfoot && (
            <tfoot>
              {getFooterGroups()?.map((footer: any, tfKey: number) => (
                <tr key={tfKey}>
                  {footer.headers?.map((tf: any, key: number) => (
                    <th
                      key={key}
                      className="p-3  text-left group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
                    >
                      {flexRender(tf.column.columnDef.header, tf.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>

      <Pagination
        isPagination={isPagination}
        data={data}
        getState={getState}
        previousPage={previousPage}
        nextPage={nextPage}
        setPageIndex={setPageIndex}
        getPageOptions={getPageOptions}
        getCanPreviousPage={getCanPreviousPage}
        getCanNextPage={getCanNextPage}
        PaginationClassName="your-custom-class"
      />
    </Fragment>
  );
};

export default TableContainer;
